import axios from 'axios'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { extractErrorMessage } from '../utils/apitools'
import useAccount from './useAccount'
import useSnackbar from './useSnackbar'
import { API_BASE_URL } from '../constants/system'
import { HelpTour } from '../types/gotypes'

interface ListToursResponse {
  tours: HelpTour[]
  count: number
}

export type HelpTourDraft = Pick<HelpTour, 'name' | 'agenda' | 'steps' | 'sort_order'>

/**
 * Tours (#1323): the named "show me around" agendas for a customer. Read by
 * the Show me around picker (anyone signed in), written by Managers on the
 * Tours tab of System context (the old Help & tours page).
 */
export const useHelpTours = ({ customer, enabled = true }: { customer: string; enabled?: boolean }) => {
  const account = useAccount()
  const snackbar = useSnackbar()
  const queryClient = useQueryClient()
  const listKey = ['help-tours', customer]

  const listQuery = useQuery({
    queryKey: listKey,
    enabled: Boolean(enabled && customer && account.user),
    queryFn: async () => {
      try {
        const res = await axios.get<ListToursResponse>(`${API_BASE_URL}/help/${customer}/tours`)
        return res.data.tours
      } catch (error) {
        throw new Error(extractErrorMessage(error))
      }
    },
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: listKey })

  const createMutation = useMutation({
    mutationFn: async (draft: HelpTourDraft) => (await axios.post<HelpTour>(`${API_BASE_URL}/help/${customer}/tours`, draft)).data,
    onSuccess: () => { invalidate(); snackbar.success('Tour saved') },
    onError: (error) => snackbar.error(extractErrorMessage(error)),
  })
  const updateMutation = useMutation({
    mutationFn: async ({ id, ...draft }: HelpTourDraft & { id: string }) =>
      (await axios.put<HelpTour>(`${API_BASE_URL}/help/${customer}/tours/${id}`, draft)).data,
    onSuccess: () => { invalidate(); snackbar.success('Tour saved') },
    onError: (error) => snackbar.error(extractErrorMessage(error)),
  })
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => { await axios.delete(`${API_BASE_URL}/help/${customer}/tours/${id}`) },
    onSuccess: () => { invalidate(); snackbar.success('Tour deleted') },
    onError: (error) => snackbar.error(extractErrorMessage(error)),
  })

  return {
    tours: listQuery.data ?? [],
    isLoading: listQuery.isLoading,
    error: listQuery.error ? listQuery.error.message : null,
    handleCreate: createMutation.mutateAsync,
    handleUpdate: updateMutation.mutateAsync,
    handleDelete: deleteMutation.mutateAsync,
    isSaving: createMutation.isPending || updateMutation.isPending,
  }
}
